import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/toaster";
import axios from 'axios';

// Public Pages
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import HowItWorks from "./components/HowItWorks";
import LegalAssistant from "./components/LegalAssistant";
import LegalIssues from "./components/LegalIssues";
import Pricing from "./components/Pricing";
import Testimonials from "./components/Testimonials";
import FAQ from "./components/FAQ";
import Footer from "./components/Footer";

// Auth Pages
import AuthPage from "./pages/AuthPage";
import RegistrationPage from "./pages/RegistrationPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import { PaymentCancelPage } from "./pages/PaymentPages";

// Dashboard Pages
import DashboardLayout from "./components/dashboard/DashboardLayout";
import ChatInterface from "./components/dashboard/ChatInterface";
import AdminSettings from "./components/dashboard/AdminSettings";
import AdminPortal from "./components/dashboard/AdminPortal";
import AccountPage from "./components/dashboard/AccountPage";
import SovereignToolbox from "./components/dashboard/SovereignToolbox";
import DocumentVault from "./components/dashboard/DocumentVault";
import DocumentVaultPage from "./components/dashboard/DocumentVaultPage";
import LegalSimulations from "./components/dashboard/LegalSimulations";
import PublicNoticeBoard from "./components/dashboard/PublicNoticeBoard";
import SubscriptionHub from "./components/dashboard/SubscriptionHub";
import SupportTickets from "./components/dashboard/SupportTickets";
import ContractPortal from "./components/dashboard/ContractPortal";
import ContractAnalysis from "./components/dashboard/ContractAnalysis";
import ComplaintPortal from "./components/dashboard/ComplaintPortal";
import RedressPortal from "./components/dashboard/RedressPortal";
import ForeclosureDefender from "./components/dashboard/ForeclosureDefender";
import RemedyAgents from "./components/dashboard/RemedyAgents";
import PublicNoticesPage from "./pages/PublicNoticesPage";
import PublicNoticeViewPage from "./pages/PublicNoticeViewPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/auth" replace />;
};

const Home = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <HowItWorks />
        <LegalAssistant />
        <LegalIssues />
        <Pricing />
        <Testimonials />
        <FAQ />
      </main>
      <Footer />
    </div>
  );
};

const DashboardHome = () => {
  const navigate = useNavigate();
  const [showSupportNotice, setShowSupportNotice] = React.useState(false);
  
  // Catch upgrade success redirect and verify payment
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const upgrade = params.get('upgrade');
    
    if (sessionId && upgrade === 'success') {
      const verifyUpgrade = async () => {
        try {
          const API_BASE = process.env.REACT_APP_BACKEND_URL;
          const res = await fetch(`${API_BASE}/api/payment/session/${sessionId}`);
          if (res.ok) {
            alert('Subscription upgraded successfully! Your account has been updated.');
            // Clean URL and redirect to subscription page
            window.history.replaceState({}, '', '/dashboard');
            window.location.reload();
          }
        } catch (err) {
          console.error('Upgrade verification error:', err);
        }
      };
      verifyUpgrade();
    }
  }, []);

  React.useEffect(() => {
    const hasSeenNotice = localStorage.getItem('hasSeenAgentPromo');
    if (!hasSeenNotice) {
      const timer = setTimeout(() => {
        setShowSupportNotice(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCloseSupportNotice = (dontShowAgain = false) => {
    if (dontShowAgain) {
      localStorage.setItem('hasSeenAgentPromo', 'true');
    }
    setShowSupportNotice(false);
  };

  const handleGoToSupport = () => {
    localStorage.setItem('hasSeenAgentPromo', 'true');
    setShowSupportNotice(false);
    navigate('/dashboard/remedy-agents');
  };
  
  const createNewCase = async () => {
    try {
      const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api` || `${import.meta.env.REACT_APP_BACKEND_URL}/api`;
      const response = await axios.post(`${API_BASE}/conversations`, {
        title: 'New Case Matter'
      });
      
      const newConversation = response.data;
      navigate(`/dashboard/chat/${newConversation.id}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  return (
    <div className="flex items-center justify-center h-full">
      {/* New AI Agents Promo Popup */}
      {showSupportNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 relative animate-fade-in">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-amber-100 rounded-full p-3">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 text-center mb-3">
              New AI Remedy Agents Available
            </h3>
            
            <p className="text-gray-600 text-center mb-4 text-sm">
              We've launched powerful new AI agents to help you defend your rights:
            </p>

            <div className="space-y-3 mb-5">
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                <div>
                  <p className="font-semibold text-amber-900 text-sm">The Mortgage Equalizer</p>
                  <p className="text-xs text-amber-700 mt-0.5">Investigate your mortgage, demand verification, and build a documented administrative record with AI-generated legal documents.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-sky-50 rounded-lg border border-sky-200">
                <svg className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10M13 16H3m10 0h4a2 2 0 002-2V9a2 2 0 00-2-2h-1.5" /></svg>
                <div>
                  <p className="font-semibold text-sky-900 text-sm">Auto Loan Remedy Agent</p>
                  <p className="text-xs text-sky-700 mt-0.5">Examine your auto loan contract, challenge disputed charges, and generate a complete 30-day administrative process.</p>
                </div>
              </div>
            </div>

            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-center mb-4 font-medium">These new Remedy AI Agents are exclusively available for Sovereign Shield and Defender of the People Memberships.</p>
            
            <div className="space-y-3">
              <button
                onClick={handleGoToSupport}
                className="w-full bg-amber-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-amber-700 transition-colors duration-200 shadow-sm flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Explore Remedy AI Agents
              </button>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleCloseSupportNotice(false)}
                  className="flex-1 bg-gray-100 text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Maybe Later
                </button>
                <button
                  onClick={() => handleCloseSupportNotice(true)}
                  className="flex-1 bg-gray-100 text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Don't Show Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="text-center max-w-2xl px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Welcome to Your Dashboard
        </h1>
        <p className="text-gray-600 mb-6">
          Start a new matter to get AI-powered assistance, or browse your existing conversations.
        </p>
        
        {/* Start a New Case Button */}
        <button
          onClick={createNewCase}
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm mb-8"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Start a New Case
        </button>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="font-semibold mb-2">AI Analysis</h3>
            <p className="text-gray-600">Get intelligent analysis of your matters with personalized recommendations.</p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="font-semibold mb-2">Document Generation</h3>
            <p className="text-gray-600">Create custom documents and templates tailored to your situation.</p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="font-semibold mb-2">Secure Storage</h3>
            <p className="text-gray-600">Keep all your documents organized and securely stored in your vault.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

import PWAInstallPrompt from "./components/PWAInstallPrompt";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/register" element={<RegistrationPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/cancel" element={<PaymentCancelPage />} />
            <Route path="/public-notices" element={<PublicNoticesPage />} />
            <Route path="/public-notices/:id" element={<PublicNoticeViewPage />} />
            
            {/* Protected Dashboard Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path="chat/:conversationId" element={<ChatInterface />} />
              <Route path="sovereign-toolbox" element={<SovereignToolbox />} />
              <Route path="public-notice-board" element={<PublicNoticeBoard />} />
              <Route path="admin" element={<AdminSettings />} />
              <Route path="admin-portal" element={<AdminPortal />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="subscription" element={<SubscriptionHub />} />
              <Route path="support" element={<SupportTickets />} />
              <Route path="documents" element={<DocumentVault />} />
              <Route path="document-vault" element={<DocumentVaultPage />} />
              <Route path="legal-simulations" element={<LegalSimulations />} />
              <Route path="contract-portal" element={<ContractPortal />} />
              <Route path="contract-analysis/:contractId" element={<ContractAnalysis />} />
              <Route path="complaint-portal/:conversationId" element={<ComplaintPortal />} />
              <Route path="redress-portal" element={<RedressPortal />} />
              <Route path="foreclosure-defender" element={<ForeclosureDefender />} />
              <Route path="remedy-agents" element={<RemedyAgents />} />
            </Route>
            
            {/* Catch all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster />
          <PWAInstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;